import {
  isAssigned,
  isOpen,
  useAlertTypeTranslation,
} from '@backoffice-frontend/alerts-common'
import { FleetMapAreaDataTestIds } from '@backoffice-frontend/common'
import { useTheme } from '@backoffice-frontend/patterns'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import {
  BellIcon,
  BellPlusIcon,
  PersonArrowIcon,
  PersonIcon,
} from '@moia-dev/pace-icons'
import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@moia-dev/pace-web'
import type {
  AlertAssignmentIconAdAlertFragment,
  AlertAssignmentIconTaskFragment,
} from './AlertAssignmentIcon.hook'

export const AlertAssignmentIcon = ({
  color,
  className,
  enableTooltip,

  alerts,
}: {
  color?: string
  className?: string
  enableTooltip?: boolean

  alerts: (
    | AlertAssignmentIconTaskFragment
    | AlertAssignmentIconAdAlertFragment
  )[]
}) => {
  const theme = useTheme()
  const getAlertTypeTranslation = useAlertTypeTranslation()
  const { user: loggedInUser } = useBackofficeLoggedInUser()

  if (!alerts || alerts.length === 0 || !loggedInUser) {
    return null
  }

  if (alerts.length === 1) {
    const onlyAlert = alerts[0]

    return (
      <Tooltip placement="top" disabled={!enableTooltip}>
        <TooltipTrigger>
          <div className={className}>
            {isOpen(onlyAlert) && (
              <Icon
                css={{
                  display: 'flex',
                }}
                size={20}
              >
                <BellIcon
                  data-testid={FleetMapAreaDataTestIds.Drawer.Alerts.BellIcon}
                  color={color ?? theme.semantic.ColorWarningDefault}
                />
              </Icon>
            )}
            {isAssigned(onlyAlert, loggedInUser.id) && (
              <Icon size={20}>
                <PersonArrowIcon
                  data-testid={FleetMapAreaDataTestIds.Drawer.Alerts.AssignIcon}
                />
              </Icon>
            )}
            {/* TODO: This icon is inconsistent. Align this icon with the user shape used in for example "AssignIcon". */}
            {!isOpen(onlyAlert) && !isAssigned(onlyAlert, loggedInUser.id) && (
              <Icon size={20}>
                <PersonIcon
                  data-testid={
                    FleetMapAreaDataTestIds.Drawer.Alerts.EmployeeIcon
                  }
                />
              </Icon>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>{getAlertTypeTranslation(onlyAlert)}</TooltipContent>
      </Tooltip>
    )
  }

  if (alerts.length > 1) {
    const tooltipContent = alerts.map(alert => {
      return (
        <div key={alert.id} css={{ display: 'flex', alignItems: 'center' }}>
          - {getAlertTypeTranslation(alert)}
        </div>
      )
    })

    return (
      <Tooltip placement="top" disabled={!enableTooltip}>
        <TooltipTrigger>
          <div className={className}>
            <Icon size={20} css={{ display: 'flex' }}>
              <BellPlusIcon
                color={color ?? theme.semantic.ColorWarningDefault}
              />
            </Icon>
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    )
  }

  return null
}
